import { useCallback, useEffect, useState } from "react";
import { Button, Field } from "@/components/ui";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";

interface Version {
  id: number;
  kind: "auto" | "manual" | "restore" | "pre-restore";
  label: string | null;
  cardCount: number;
  sizeBytes: number;
  createdAt: string;
}

/** Version history: automatic snapshots plus named ones; restore replaces the board (a safety copy is kept). */
export function VersionsPanel({
  boardId,
  canEdit,
  onClose,
}: {
  boardId: string;
  canEdit: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    async () => setVersions(await api<Version[]>(`/api/boards/${boardId}/versions`)),
    [boardId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      await api(`/api/boards/${boardId}/versions`, { method: "POST", json: { label: label || undefined } });
      setLabel("");
      await load();
    } finally {
      setBusy(false);
    }
  };
  const restore = async (v: Version) => {
    if (!confirm(t.confirmRestore(new Date(v.createdAt).toLocaleString()))) return;
    setBusy(true);
    try {
      await api(`/api/boards/${boardId}/versions/${v.id}/restore`, { method: "POST" });
      await load();
    } finally {
      setBusy(false);
    }
  };
  return (
    <aside className="absolute bottom-0 right-0 top-0 z-30 flex max-md:w-full md:w-96 flex-col border-l border-border bg-panel shadow-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="display text-xl">.{t.history}</span>
        <button type="button" onClick={onClose} className="text-muted hover:text-text">
          ✕
        </button>
      </div>
      {canEdit ? (
        <div className="flex items-end gap-2 border-b border-border px-4 py-3">
          <Field
            label={t.versionLabel}
            name="label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="flex-1"
            placeholder={t.versionLabelPlaceholder}
          />
          <Button className="px-3 py-2 text-xs" onClick={save} loading={busy}>
            {t.saveVersion}
          </Button>
        </div>
      ) : null}
      <ul className="flex-1 overflow-auto">
        {versions?.length === 0 ? <li className="p-4 text-sm text-dim">{t.noVersions}</li> : null}
        {versions?.map((v) => (
          <li key={v.id} className="flex items-center gap-3 border-b border-border px-4 py-2 text-sm">
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{v.label || t.versionKinds[v.kind]}</span>
              <span className="text-[11px] text-dim">
                {new Date(v.createdAt).toLocaleString()} · {v.cardCount} {t.cards}
              </span>
            </span>
            {canEdit ? (
              <Button
                variant="ghost"
                className="px-2 py-1 text-xs"
                onClick={() => restore(v)}
                disabled={busy}
              >
                {t.restore}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </aside>
  );
}
